import {Component, computed, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {productCompositionsResourceType} from "./configs/product-compositions-modal-config";
import {ProductCompositionsModalService} from "./services/product-compositions-modal.service";
import {
  ProductCompositionsData,
  ProductCompositionsDialogData,
  ProductCompositionsFormField
} from "./types/product-compositions-modal.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {ProductCardFormFieldService} from "../../../services/product-card-form-field.service";
import {FormGroup} from "@angular/forms";
import {LoadingService} from "../../../../../../core/services/loading.service";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {GetDataInModalParams} from "../../../types/product-card-modals.types";
import {productCompositionFormFields} from "./configs/product-composition-form-fields";
import {defaultProductCompositionsData} from "./configs/default-product-compositions-data";
import {ProductCompositionFilesStateService} from "./services/product-composition-files-state.service";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {MatButton, MatMiniFabButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {
  ProductCompositionsFormComponent
} from "./components/product-compositions-form/product-compositions-form.component";
import {
  ProductCompositionsFilesComponent
} from "./components/product-compositions-files/product-compositions-files.component";

@Component({
  selector: 'app-product-compositions-modal',
  imports: [
    BaseModalComponent,
    MatAccordion,
    MatButton,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatIcon,
    MatMiniFabButton,
    ProductCompositionsFormComponent,
    ProductCompositionsFilesComponent
  ],
  templateUrl: './product-compositions-modal.component.html',
  styleUrl: './product-compositions-modal.component.scss',
  providers: [ LoadingService, EditModeService ]
})
export class ProductCompositionsModalComponent extends BaseModalComponent {
  private productCompositionsDialogData = inject<ProductCompositionsDialogData>(MAT_DIALOG_DATA);
  private productCompositionsModalService: ProductCompositionsModalService = inject(ProductCompositionsModalService);
  private productCardFormFieldService: ProductCardFormFieldService = inject(ProductCardFormFieldService);
  private productCompositionFilesStateService: ProductCompositionFilesStateService = inject(ProductCompositionFilesStateService);

  public readonly productCompositionsResourceType = productCompositionsResourceType;
  public productCompositionFormGroups = signal<Array<FormGroup>>([]);
  public productCompositionsData = signal<Array<ProductCompositionsData>>([]);
  public isValid = signal<boolean>(false);
  public noFirstData = signal<boolean>(false);
  public technicalParametersId = computed(() => Number(this.productCompositionsDialogData.productCompositions.id));
  public parentId = computed(() => this.productCompositionsDialogData.parentId);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.productCompositionsResourceType.key);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<ProductCompositionsFormField>(productCompositionFormFields);
    this.productCompositionFormGroups.update(forms  => [ ...forms, formGroup ]);
    this.productCompositionsData.update(data  => [ ...data, defaultProductCompositionsData ]);
    this.isValid.set(false);
  }

  public updateProductCompositionData(data: ProductCompositionsData, index: number): void {
    this.productCompositionsData()[index] = data;
    this.productCompositionFormGroups().at(index)?.patchValue(data);
    this.isValid.set(!!data.id);
    this.noFirstData.set(!!this.productCompositionsData().length);
  }

  public deleteProductComposition(index: number): void {
    this.productCompositionsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.productCompositionFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.isValid.set(true);
  }

  public async handleDelete(event: MouseEvent, id: number, index: number): Promise<void> {
    event.stopPropagation();
    const isConfirmed = await this.productCardFormFieldService.isDeleteConfirmed(id);
    if (!isConfirmed) { return; }

    this.loadingService.setIsLoadingState(true);
    await this.deleteRecord(productCompositionsResourceType.path, id)
      .then(() => this.removeProductCompositionAtIndex(index, id))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async initModal(): Promise<void> {
    this.modalStatesService.openModal(this.productCompositionsResourceType.key);
    this.loadingService.setIsLoadingState(true);
    await this.loadProductCompositionData();
  }

  private async loadProductCompositionData(): Promise<void> {
    const params: GetDataInModalParams = {
      path: productCompositionsResourceType.path,
      queryParams: {
        technicalParametersId: Number(this.productCompositionsDialogData.productCompositions.id)
      }
    };

    await this.productCompositionsModalService.getDataInModal<ProductCompositionsData>(params)
      .then(data => this.successLoad(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private successLoad(data: Array<ProductCompositionsData>): void {
    this.productCompositionsData.set(data);
    this.isValid.set(!!this.productCompositionsData()?.at(0)?.id);
    this.noFirstData.set(!!this.productCompositionsData()?.at(0)?.id);
    this.initForm();
  }

  private initForm(): void {
    if (!this.productCompositionsData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.productCompositionsData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<ProductCompositionsFormField>(productCompositionFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.productCompositionFormGroups.set(formGroups);
  }

  private removeProductCompositionAtIndex(index: number, id: number): void {
    this.productCompositionFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.productCompositionsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.productCompositionFilesStateService.deleteProductCompositionFilesState(id);
    this.isValid.set(!!this.productCompositionsData().length);
    if (!this.productCompositionsData().length) {
      this.noFirstData.set(false);
      this.handleAddNew();
    }
  }
}
